'use client';

import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
  useCallback,
} from 'react';
import { createClient } from '@/lib/supabase/client';
import { useLanguage } from './LanguageContext';

interface HeroData {
  name: string;
  title: string;
  skill_name: string;
  bio: string;
  cv_url: string;
  linkedin_url: string;
}

interface Skill {
  id: string;
  name: string;
  category: string;
  color: string;
  bg_color: string;
  border_color: string;
  sort_order: number;
}

interface Project {
  id: string;
  title: string;
  description: string;
  tech_stack: string[];
  live_url: string;
  github_url: string;
  image_url: string;
  featured: boolean;
  status: string;
  sort_order: number;
}

interface Experience {
  id: string;
  company: string;
  role: string;
  period: string;
  description: string;
  highlights: string[];
  work_type: string;
  location: string;
  is_current: boolean;
  sort_order: number;
}

interface ContactInfo {
  email: string;
  phone: string;
  location: string;
  linkedin_url: string;
  website_url: string;
  cv_url: string;
}

interface SiteSettings {
  logo_url: string;
  site_name: string;
  meta_title?: string;
  meta_keywords?: string;
  meta_description: string;
  ga_measurement_id?: string;
  ga_property_id?: string;
  ga_client_email?: string;
  ga_private_key?: string;
  tech_stack_line?: string;
  footer_brand_name?: string;
  footer_initials?: string;
  calendly_url?: string;
  resend_api_key?: string;
}

interface PortfolioData {
  hero: HeroData | null;
  skills: Skill[];
  projects: Project[];
  experience: Experience[];
  contact: ContactInfo | null;
  siteSettings: SiteSettings | null;
  loading: boolean;
}

const PortfolioDataContext = createContext<PortfolioData>({
  hero: null,
  skills: [],
  projects: [],
  experience: [],
  contact: null,
  siteSettings: null,
  loading: true,
});

export function PortfolioDataProvider({ children }: { children: ReactNode }) {
  const { language } = useLanguage();
  const [hero, setHero] = useState<HeroData | null>(null);
  const [skills, setSkills] = useState<Skill[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [experience, setExperience] = useState<Experience[]>([]);
  const [contact, setContact] = useState<ContactInfo | null>(null);
  const [siteSettings, setSiteSettings] = useState<SiteSettings | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const supabase = createClient();

      const [heroRes, skillsRes, projectsRes, expRes, contactRes, settingsRes] = await Promise.all([
        supabase.from('hero_content').select('*').eq('lang', language).maybeSingle(),
        supabase.from('skills').select('*').order('sort_order'),
        supabase.from('projects').select('*').eq('lang', language).order('sort_order'),
        supabase.from('experience').select('*').eq('lang', language).order('sort_order'),
        supabase.from('contact_info').select('*').limit(1).maybeSingle(),
        supabase.from('site_settings').select('*').limit(1).maybeSingle(),
      ]);

      if (heroRes.data) setHero(heroRes.data);
      if (skillsRes.data) setSkills(skillsRes.data);
      if (projectsRes.data) setProjects(projectsRes.data);
      if (expRes.data) setExperience(expRes.data);
      if (contactRes.data) setContact(contactRes.data);
      if (settingsRes.data) setSiteSettings(settingsRes.data);
    } catch (err: unknown) {
      console.error('Portfolio data fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [language]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  return (
    <PortfolioDataContext.Provider
      value={{ hero, skills, projects, experience, contact, siteSettings, loading }}
    >
      {children}
    </PortfolioDataContext.Provider>
  );
}

export function usePortfolioData() {
  return useContext(PortfolioDataContext);
}
